// ===================================================================
// KAIXO - Pestaña Facturas
// ===================================================================
// CRUD básico de facturas con líneas, IVA y estados.
// Numeración secuencial automática por año.
// ===================================================================

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    adminInvoicesList, adminInvoiceCreate,
    adminInvoiceMarkPaid, adminInvoiceVoid,
    adminUsersList, adminUsersBillingData
} from '../../../services/adminApi';
import './AdminInvoicesSection.css';

function fmtMoney(c, currency = 'USD') {
    return ((c || 0) / 100).toLocaleString('en-US', { style: 'currency', currency });
}

const STATUS_LABELS = {
    draft:  { label: 'Borrador', color: '#888' },
    issued: { label: 'Emitida',  color: '#3b82f6' },
    paid:   { label: 'Pagada',   color: '#22c55e' },
    voided: { label: 'Anulada',  color: '#ef4444' }
};

export default function AdminInvoicesSection() {
    const [invoices, setInvoices] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [creating, setCreating] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await adminInvoicesList(statusFilter ? { status: statusFilter } : {});
            setInvoices(data?.invoices || []);
            setStats(data?.stats || null);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { reload(); }, [reload]);

    const handleAction = async (id, action) => {
        try {
            if (action === 'paid') await adminInvoiceMarkPaid(id);
            if (action === 'void' && window.confirm('¿Anular esta factura? No se podrá modificar.')) {
                await adminInvoiceVoid(id);
            }
            reload();
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    };

    return (
        <div className="ainv-wrap">
            <div className="ainv-toolbar">
                <select className="ainv-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="">Todos los estados</option>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                    ))}
                </select>
                <button className="ainv-btn-primary" onClick={() => setCreating(true)}>
                    + Nueva factura
                </button>
            </div>

            {stats && (
                <div className="ainv-stats">
                    <div>
                        <div className="ainv-stat-num">{fmtMoney(stats.totalCents)}</div>
                        <div className="ainv-stat-lbl">Facturado {stats.year}</div>
                    </div>
                    <div>
                        <div className="ainv-stat-num">{fmtMoney(stats.taxCents)}</div>
                        <div className="ainv-stat-lbl">IVA repercutido {stats.year}</div>
                    </div>
                    <div>
                        <div className="ainv-stat-num">{stats.count}</div>
                        <div className="ainv-stat-lbl">Facturas {stats.year}</div>
                    </div>
                </div>
            )}

            {loading && <div className="ap-loading">Cargando…</div>}
            {error && <div className="ap-error">{error}</div>}

            {!loading && (
                <table className="ainv-table">
                    <thead>
                        <tr>
                            <th>Nº</th>
                            <th>Cliente</th>
                            <th>Fecha</th>
                            <th>Total</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoices.map(inv => {
                            const s = STATUS_LABELS[inv.status] || STATUS_LABELS.draft;
                            return (
                                <tr key={inv._id}>
                                    <td><strong>{inv.number}</strong></td>
                                    <td>{inv.customer?.name || '—'}</td>
                                    <td>{new Date(inv.issueDate).toLocaleDateString()}</td>
                                    <td>{fmtMoney(inv.totalCents, inv.currency)}</td>
                                    <td>
                                        <span className="ainv-status" style={{ background: s.color + '22', color: s.color }}>
                                            {s.label}
                                        </span>
                                    </td>
                                    <td>
                                        {inv.status === 'issued' && (
                                            <button className="ainv-btn-mini-success" onClick={() => handleAction(inv._id, 'paid')}>
                                                Marcar pagada
                                            </button>
                                        )}
                                        {['draft', 'issued'].includes(inv.status) && (
                                            <button className="ainv-btn-mini-danger" onClick={() => handleAction(inv._id, 'void')}>
                                                Anular
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {invoices.length === 0 && (
                            <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                                Sin facturas todavía.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            )}

            {creating && (
                <InvoiceForm
                    onCancel={() => setCreating(false)}
                    onSaved={() => { setCreating(false); reload(); }}
                />
            )}
        </div>
    );
}

function InvoiceForm({ onCancel, onSaved }) {
    const [customer, setCustomer] = useState({ name: '', taxId: '', address: '', country: '', email: '' });
    const [items, setItems] = useState([{ description: '', quantity: 1, unitPrice: '', taxPct: 21 }]);
    const [currency, setCurrency] = useState('USD');
    const [dueDate, setDueDate] = useState('');
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    // Buscador de clientes (users de KAIXO) para prerellenar el cliente.
    // Combina User + Verification approved (si tiene) para sacar nombre,
    // email, país, taxId. Address sigue siendo manual (no se persiste hoy).
    const [customerQuery, setCustomerQuery] = useState('');
    const [customerResults, setCustomerResults] = useState([]);
    const [searchingCustomer, setSearchingCustomer] = useState(false);
    const [prefilling, setPrefilling] = useState(false);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const q = customerQuery.trim();
        if (!q || q.length < 2) {
            setCustomerResults([]);
            setSearchingCustomer(false);
            return;
        }
        setSearchingCustomer(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const { data } = await adminUsersList({ q, limit: 8 });
                setCustomerResults(data?.users || []);
            } catch {
                setCustomerResults([]);
            } finally {
                setSearchingCustomer(false);
            }
        }, 300);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [customerQuery]);

    const prefillFromUser = async (userId) => {
        setPrefilling(true);
        setErr('');
        try {
            const { data } = await adminUsersBillingData(userId);
            const b = data?.billing;
            if (b) {
                setCustomer(c => ({
                    name: b.name || c.name,
                    taxId: b.taxId || c.taxId,
                    country: b.country || c.country,
                    email: b.email || c.email,
                    // address se mantiene si ya tenía algo, no lo sobrescribimos
                    // (el endpoint no lo devuelve hoy)
                    address: c.address
                }));
            }
            // Limpiar el buscador tras prerellenar
            setCustomerQuery('');
            setCustomerResults([]);
        } catch (e) {
            setErr(e.response?.data?.error || e.message);
        } finally {
            setPrefilling(false);
        }
    };

    const updateItem = (i, field, val) => {
        setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [field]: val } : it));
    };
    const addItem = () => setItems(prev => [...prev, { description: '', quantity: 1, unitPrice: '', taxPct: 21 }]);
    const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));

    const totals = items.reduce((acc, it) => {
        const line = (parseFloat(it.unitPrice) || 0) * (parseInt(it.quantity, 10) || 0);
        const tax = line * (parseFloat(it.taxPct) || 0) / 100;
        return { sub: acc.sub + line, tax: acc.tax + tax };
    }, { sub: 0, tax: 0 });

    const submit = async () => {
        setErr('');
        if (!customer.name.trim()) { setErr('Nombre del cliente obligatorio'); return; }
        if (items.some(it => !it.description || !it.unitPrice)) {
            setErr('Cada línea necesita descripción y precio'); return;
        }
        const data = {
            customer,
            items: items.map(it => ({
                description: it.description,
                quantity: parseInt(it.quantity, 10),
                unitPriceCents: Math.round(parseFloat(it.unitPrice) * 100),
                taxPct: parseFloat(it.taxPct) || 0
            })),
            currency,
            dueDate: dueDate || null,
            notes
        };
        setBusy(true);
        try {
            await adminInvoiceCreate(data);
            onSaved();
        } catch (e) {
            setErr(e.response?.data?.error || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="ainv-modal-overlay" onClick={onCancel}>
            <div className="ainv-modal" onClick={e => e.stopPropagation()}>
                <h3>Nueva factura</h3>

                <h4>Cliente</h4>

                {/* Buscador de cliente — escribe nombre/email/teléfono y al
                    seleccionar uno, se prerellenan los campos con sus datos
                    (User + Verification approved si la tiene). */}
                <div className="ainv-customer-search">
                    <input
                        className="ainv-input"
                        placeholder="🔍 Buscar cliente existente (nombre, email, teléfono)…"
                        value={customerQuery}
                        onChange={e => setCustomerQuery(e.target.value)}
                        disabled={prefilling}
                    />
                    {searchingCustomer && <div className="ainv-search-status">Buscando…</div>}
                    {!searchingCustomer && customerResults.length > 0 && (
                        <div className="ainv-search-results">
                            {customerResults.map(u => (
                                <button
                                    key={u._id}
                                    type="button"
                                    className="ainv-search-card"
                                    onClick={() => prefillFromUser(u._id)}
                                    disabled={prefilling}
                                    title="Usar datos de este cliente"
                                >
                                    <strong>{u.name || '—'}</strong>
                                    <span>{u.phone || u.email || u._id}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {!searchingCustomer && customerQuery.trim().length >= 2 && customerResults.length === 0 && (
                        <div className="ainv-search-status">Sin coincidencias.</div>
                    )}
                    {prefilling && <div className="ainv-search-status">Cargando datos del cliente…</div>}
                </div>

                <input className="ainv-input" placeholder="Nombre / Razón social"
                       value={customer.name}
                       onChange={e => setCustomer(c => ({ ...c, name: e.target.value }))} />
                <div className="ainv-row">
                    <input className="ainv-input" placeholder="Tax ID / CIF / RUC"
                           value={customer.taxId}
                           onChange={e => setCustomer(c => ({ ...c, taxId: e.target.value }))} />
                    <input className="ainv-input" placeholder="País"
                           value={customer.country}
                           onChange={e => setCustomer(c => ({ ...c, country: e.target.value }))} />
                </div>
                <input className="ainv-input" placeholder="Dirección"
                       value={customer.address}
                       onChange={e => setCustomer(c => ({ ...c, address: e.target.value }))} />
                <input className="ainv-input" placeholder="Email"
                       value={customer.email}
                       onChange={e => setCustomer(c => ({ ...c, email: e.target.value }))} />

                <h4>Líneas</h4>
                {items.map((it, i) => (
                    <div key={i} className="ainv-item-row">
                        <input className="ainv-input" placeholder="Descripción"
                               value={it.description}
                               onChange={e => updateItem(i, 'description', e.target.value)} />
                        <input className="ainv-input ainv-input-tiny" type="number" min="1"
                               value={it.quantity}
                               onChange={e => updateItem(i, 'quantity', e.target.value)} />
                        <input className="ainv-input ainv-input-small" type="number" step="0.01" placeholder="Precio"
                               value={it.unitPrice}
                               onChange={e => updateItem(i, 'unitPrice', e.target.value)} />
                        <input className="ainv-input ainv-input-tiny" type="number" placeholder="IVA%"
                               value={it.taxPct}
                               onChange={e => updateItem(i, 'taxPct', e.target.value)} />
                        {items.length > 1 && (
                            <button className="ainv-btn-mini-danger" onClick={() => removeItem(i)}>✕</button>
                        )}
                    </div>
                ))}
                <button className="ainv-btn-add" onClick={addItem}>+ Añadir línea</button>

                <div className="ainv-totals">
                    <div>Subtotal: <strong>{totals.sub.toFixed(2)} {currency}</strong></div>
                    <div>IVA: <strong>{totals.tax.toFixed(2)} {currency}</strong></div>
                    <div>Total: <strong>{(totals.sub + totals.tax).toFixed(2)} {currency}</strong></div>
                </div>

                <div className="ainv-row">
                    <select className="ainv-input" value={currency} onChange={e => setCurrency(e.target.value)}>
                        <option>USD</option><option>EUR</option><option>PEN</option><option>GBP</option>
                    </select>
                    <input className="ainv-input" type="date" placeholder="Vencimiento"
                           value={dueDate} onChange={e => setDueDate(e.target.value)} />
                </div>

                <textarea className="ainv-input" rows={2} placeholder="Notas"
                          value={notes} onChange={e => setNotes(e.target.value)} />

                {err && <div className="ainv-error">{err}</div>}

                <div className="ainv-modal-actions">
                    <button className="ainv-btn-secondary" onClick={onCancel}>Cancelar</button>
                    <button className="ainv-btn-primary" onClick={submit} disabled={busy}>
                        {busy ? 'Creando…' : 'Crear factura'}
                    </button>
                </div>
            </div>
        </div>
    );
}
